import { getContext, setContext } from 'svelte';
import type { NavigationHistory } from '@shared/types/History.ts';
import { createTileListOnEx } from '@tilelist/application/core/OnExFactory.ts';
import type { TileListInitialSession } from '@tilelist/application/core/TileListInitialSession.ts';
import type { TileListPageLoadResult } from '@tilelist/application/core/TileListSession.ts';
import type { EventsIncoming } from '@tilelist/application/ports/EventsIncoming.ts';
import type { EventsOutgoing } from '@tilelist/application/ports/EventsOutgoing.ts';
import type { TrackingPort } from '@tilelist/application/ports/TrackingPort.ts';
import type { VariationLoader } from '@tilelist/application/ports/VariationLoader.ts';
import type {
    StrategyFactory,
    TileListBrowserWiring,
} from '@tilelist/application/wiring/TileListBrowserWiring.browser.ts';
import { createTileListBrowserWiring } from '@tilelist/application/wiring/TileListBrowserWiring.browser.ts';
import { ProductTileController } from '@tilelist/controllers/ProductTileController.browser.ts';
import type { OnEx } from '@tilelist/domain/experiments/OnEx.ts';

export type TileListWiring = {
    onex: OnEx;
    navigationHistoryEnabled: boolean | null;
    trackingService: TrackingPort | null;
    variationLoadingService: VariationLoader | null;
    controller: ProductTileController | null;
    eventsIncoming: EventsIncoming | null;
    eventsOutgoing: EventsOutgoing | null;
    navigationHistory: NavigationHistory | null;
    restoreScrollPosition: () => Promise<void>;
    saveScrollSelector: () => void;
};

export const WIRING_KEY: unique symbol = Symbol('TileListWiring');

/**
 *
 *
 *
 *
 *
 */
export function createAndProvideWiring(initialSession: TileListInitialSession): TileListWiring {
    const onex = createTileListOnEx(initialSession.session);
    const wiring = $state<TileListWiring>({
        onex,
        navigationHistoryEnabled: null,
        trackingService: null,
        variationLoadingService: null,
        controller: null,
        eventsIncoming: null,
        eventsOutgoing: null,
        navigationHistory: null,
        restoreScrollPosition: () => Promise.resolve(),
        saveScrollSelector: () => {},
    });
    setContext(WIRING_KEY, wiring);
    return wiring;
}

/**
 *
 *
 *
 *
 *
 */
export function mountWiring(
    ctx: TileListWiring,
    initialSession: TileListInitialSession,
    onReload: (result: TileListPageLoadResult, browserWiring: TileListBrowserWiring) => void | Promise<void>,
    strategyFactory?: StrategyFactory,
): { browserWiring: TileListBrowserWiring; cleanup: () => void } {
    const browserWiring = createTileListBrowserWiring(initialSession, strategyFactory);
    const navigationHistoryEnabled = ctx.onex.isToggleActive('REPTILE_1303_NAVIGATION_HISTORY');

    ctx.eventsIncoming = browserWiring.eventsIncoming;
    ctx.eventsOutgoing = browserWiring.eventsOutgoing;
    ctx.navigationHistory = browserWiring.navigationHistory;
    if (navigationHistoryEnabled) {
        browserWiring.navigationHistory.seed({
            rule: initialSession.page.context.rule,
            page: initialSession.page.page,
            historic: false,
            entryRule: initialSession.session.entryRule,
        });
    }
    ctx.trackingService = browserWiring.trackingService;
    ctx.variationLoadingService = browserWiring.variationLoadingService;
    ctx.restoreScrollPosition = browserWiring.restoreScrollPosition;
    ctx.saveScrollSelector = browserWiring.saveScrollSelector;
    ctx.controller = new ProductTileController(
        browserWiring.variationLoadingService,
        browserWiring.trackingService,
        initialSession.session.entryRule,
    );
    ctx.navigationHistoryEnabled = navigationHistoryEnabled;

    const unsub = browserWiring.eventsIncoming.onPageResolved(async (event) => {
        const result = await browserWiring.session.reload(event);
        await onReload(result, browserWiring);
    });

    return { browserWiring, cleanup: () => unsub() };
}

const NULL_WIRING: TileListWiring = {
    /*                                                                             */
    /*                                                                              */
    get onex(): OnEx {
        throw new Error('injectWiring: no wiring context available (onex is unset)');
    },
    navigationHistoryEnabled: null,
    trackingService: null,
    variationLoadingService: null,
    controller: null,
    eventsIncoming: null,
    eventsOutgoing: null,
    navigationHistory: null,
    restoreScrollPosition: () => Promise.resolve(),
    saveScrollSelector: () => {},
};

/*                                                                              */
type RequiredWiring<K extends keyof TileListWiring> = Omit<TileListWiring, K> & {
    [P in K]-?: NonNullable<TileListWiring[P]>;
};

/**
 *
 *
 *
 *
 *
 *
 */
export function injectWiring(): TileListWiring;
export function injectWiring<K extends keyof TileListWiring>(required: readonly K[]): RequiredWiring<K>;
export function injectWiring(_required?: readonly (keyof TileListWiring)[]): TileListWiring {
    /*                                                                                                                             */
    return getContext<TileListWiring>(WIRING_KEY) ?? NULL_WIRING;
}
